import React, { useState } from 'react';
import { Standings } from '../components/Standings';
import { AwardRace, AwardCeremony } from '../components/AwardRace';
import { CompetitionAwards } from '../components/CompetitionAwards';
import { MatchHighlightsModal } from '../components/MatchHighlightsModal';
import { YouthAcademy } from '../components/YouthAcademy';
import { ScrollArea } from '../components/ui/scroll-area';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { simulateWeek, quickSimulate, updatePlayerStats, updateTeamStats, applyMatchGrowth } from '../utils/simulationEngine';
import { generateSeasonFixtures, EMPTY_STATS } from '../utils/gameData';
import { Play, FastForward, CaretLeft, CaretRight, SoccerBall, Trophy, FirstAid, ArrowUp, Eye, Lightning } from '@phosphor-icons/react';
import { MatchSimModal } from '../components/MatchSimModal';

export const LeaguePage = ({ gameState, onUpdateGameState }) => {
  const [selectedWeek, setSelectedWeek] = useState(gameState.currentWeek);
  const [isSimulating, setIsSimulating] = useState(false);
  const [showCeremony, setShowCeremony] = useState(false);
  const [highlightsMatch, setHighlightsMatch] = useState(null);
  const [simModalMatch, setSimModalMatch] = useState(null);

  const simOneFixture = (fixture) => {
    const home = getTeamById(fixture.homeTeamId);
    const away = getTeamById(fixture.awayTeamId);
    if (!home || !away) return;
    const result = quickSimulate(home, away);
    applySingleFixtureResult(fixture, home, away, result);
  };

  const applySingleFixtureResult = (fixture, home, away, result) => {
    const isHomeWin = result.homeScore > result.awayScore;
    const isDraw = result.homeScore === result.awayScore;
    let updated = teams.map(t => t);
    const hIdx = updated.findIndex(t => t.id === home.id);
    const aIdx = updated.findIndex(t => t.id === away.id);
    updated[hIdx] = updateTeamStats(updated[hIdx], result.homeScore, result.awayScore, isHomeWin, isDraw);
    updated[aIdx] = updateTeamStats(updated[aIdx], result.awayScore, result.homeScore, !isHomeWin && !isDraw, isDraw);
    updated = updatePlayerStats(updated, result.events, home.name, away.name, result.homeScore, result.awayScore, 'league');
    updated = applyMatchGrowth(updated, result.matchRatings);
    const newFixtures = gameState.fixtures.map(f => f.id === fixture.id
      ? { ...f, homeScore: result.homeScore, awayScore: result.awayScore, played: true, events: result.events, matchRatings: result.matchRatings }
      : f);
    onUpdateGameState({ ...gameState, teams: updated, fixtures: newFixtures });
  };

  const openFullSimFixture = (fixture) => {
    const home = getTeamById(fixture.homeTeamId);
    const away = getTeamById(fixture.awayTeamId);
    if (!home || !away) return;
    setSimModalMatch({ fixture, home, away });
  };

  const { teams, fixtures, currentWeek } = gameState;
  const weekFixtures = fixtures.filter(f => f.week === selectedWeek);
  const getTeamById = (id) => teams.find(t => t.id === id);

  // Count injured players
  const injuredCount = teams.reduce((sum, t) => sum + t.players.filter(p => p.injuredWeeks > 0).length, 0);

  const handleSimulateWeek = () => {
    if (currentWeek > 38) return;
    setIsSimulating(true);
    setTimeout(() => {
      const result = simulateWeek(teams, fixtures, currentWeek);
      const newWeek = currentWeek + 1;
      onUpdateGameState({ ...gameState, teams: result.teams, fixtures: result.fixtures, currentWeek: newWeek });
      setSelectedWeek(newWeek);
      setIsSimulating(false);
      if (newWeek > 38) setShowCeremony(true);
    }, 500);
  };

  const handleSimulateAll = () => {
    setIsSimulating(true);
    let updatedState = { ...gameState };
    const simulateNext = (week) => {
      if (week > 38) {
        setIsSimulating(false);
        setShowCeremony(true);
        return;
      }
      setTimeout(() => {
        const result = simulateWeek(updatedState.teams, updatedState.fixtures, week);
        updatedState = { ...updatedState, teams: result.teams, fixtures: result.fixtures, currentWeek: week + 1 };
        onUpdateGameState(updatedState);
        setSelectedWeek(week + 1);
        simulateNext(week + 1);
      }, 50);
    };
    simulateNext(currentWeek);
  };

  const handleAdvanceSeason = () => {
    const newSeason = (gameState.season || 1) + 1;
    const champion = [...teams].sort((a, b) => b.stats.points - a.stats.points)[0];
    let trophyHistory = gameState.trophyHistory || [];
    if (champion) {
      const alreadyRecorded = trophyHistory.some(h => h.season === gameState.season && h.competition === 'league');
      if (!alreadyRecorded) {
        trophyHistory = [...trophyHistory, { season: gameState.season, year: gameState.year || 2026, competition: 'league', competitionLabel: 'EWFL League', winnerId: champion.id, winnerName: champion.name }];
      }
    }
    const grownTeams = teams.map(t => ({
      ...t,
      players: t.players.map(p => ({ ...p, seasonStartOverall: p.overall, stats: EMPTY_STATS(), injuredWeeks: 0 })),
      stats: { played: 0, won: 0, drawn: 0, lost: 0, goalsFor: 0, goalsAgainst: 0, points: 0 }
    }));
    const newFixtures = generateSeasonFixtures(grownTeams);
    onUpdateGameState({
      ...gameState,
      teams: grownTeams,
      fixtures: newFixtures,
      currentWeek: 1,
      season: newSeason,
      year: 2025 + newSeason,
      cup: null, championsLeague: null, cwc: null, worldCup: null,
      trophyHistory
    });
    setSelectedWeek(1);
    setShowCeremony(false);
  };

  const handlePromoteYouth = (player) => {
    // Add youth player to first team in league (pick a random team for now)
    // In full implementation, this would be tied to a selected team
  };

  return (
    <div className="min-h-screen bg-[#04060F] pt-20 pb-8 px-4 overflow-x-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-6 gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.4em] text-[#00E5FF] mb-2"
              style={{ textShadow: '0 0 10px rgba(0,229,255,0.4)' }}>
              J · League Season
            </p>
            <h1 className="font-heading font-bold text-3xl sm:text-5xl text-white tracking-tight leading-none"
              style={{ letterSpacing: '-0.015em' }}>
              League
            </h1>
            <p className="text-[#7A86B5] text-sm mt-3 tracking-wide">
              Season <span className="text-white font-semibold">{gameState.season}</span>
              <span className="mx-2 text-[#4A5278]">·</span>
              Year <span className="text-white font-semibold">{gameState.year || 2026}</span>
              <span className="mx-2 text-[#4A5278]">·</span>
              Week <span className="text-white font-semibold">{currentWeek}/38</span>
              {injuredCount > 0 && <span className="text-[#FF4D6D] ml-3 inline-flex items-center gap-1 text-xs"><FirstAid size={13} className="inline" /> {injuredCount} injured</span>}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Button data-testid="simulate-week-btn" onClick={handleSimulateWeek} disabled={isSimulating || currentWeek > 38}
              className="bg-[#00E5FF] hover:bg-[#66F2FF] text-black font-semibold rounded-md px-4 sm:px-5 h-10"
              style={{ boxShadow: '0 0 28px rgba(0,229,255,0.5), 0 10px 24px rgba(0,0,0,0.4)' }}>
              {isSimulating ? <span className="loading-spinner mr-2" /> : <Play size={16} className="mr-2" weight="fill" />}
              Simulate Week
            </Button>
            <Button data-testid="simulate-all-btn" onClick={handleSimulateAll} disabled={isSimulating || currentWeek > 38} variant="outline"
              className="bg-transparent border border-[#1E2240] text-white hover:bg-[#0A0D1F] hover:border-[#00E5FF] rounded-md px-4 h-10 font-medium">
              <FastForward size={16} className="mr-2" /> Sim All
            </Button>
          </div>
        </div>

        <Tabs defaultValue="standings" className="w-full">
          <TabsList className="bg-[#0A0D1F] border border-[#1E2240] w-full grid grid-cols-3 mb-6 p-1 rounded-md">
            <TabsTrigger value="standings" className="data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black data-[state=active]:shadow-[0_0_20px_rgba(0,229,255,0.35)] font-heading uppercase text-sm rounded-sm transition-all" data-testid="tab-standings">
              Standings
            </TabsTrigger>
            <TabsTrigger value="awards" className="data-[state=active]:bg-[#FF006E] data-[state=active]:text-white data-[state=active]:shadow-[0_0_20px_rgba(255,0,110,0.35)] font-heading uppercase text-sm rounded-sm transition-all" data-testid="tab-awards">
              Award Races
            </TabsTrigger>
            <TabsTrigger value="stats" className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black data-[state=active]:shadow-[0_0_20px_rgba(204,255,0,0.35)] font-heading uppercase text-sm rounded-sm transition-all" data-testid="tab-league-stats">
              League Stats
            </TabsTrigger>
          </TabsList>

          <TabsContent value="standings">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2"><Standings teams={teams} /></div>
              <div className="lg:col-span-1">
                <div className="card-surface rounded-sm">
                  <div className="border-b border-[#2C2C35] px-4 py-3">
                    <div className="flex items-center justify-between">
                      <h3 className="font-heading font-bold text-white uppercase tracking-wide">Fixtures</h3>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="ghost" onClick={() => setSelectedWeek(Math.max(1, selectedWeek - 1))} disabled={selectedWeek <= 1} className="h-8 w-8 p-0 text-white hover:bg-[#1A1A1F]" data-testid="prev-week-btn"><CaretLeft size={16} /></Button>
                        <Select value={String(selectedWeek)} onValueChange={(v) => setSelectedWeek(Number(v))}>
                          <SelectTrigger className="w-[100px] h-8 bg-[#0A0A0C] border-[#2C2C35] text-white text-sm" data-testid="week-select"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-[#121216] border-[#2C2C35]">
                            {Array.from({ length: 38 }, (_, i) => i + 1).map(week => <SelectItem key={week} value={String(week)}>Week {week}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Button size="sm" variant="ghost" onClick={() => setSelectedWeek(Math.min(38, selectedWeek + 1))} disabled={selectedWeek >= 38} className="h-8 w-8 p-0 text-white hover:bg-[#1A1A1F]" data-testid="next-week-btn"><CaretRight size={16} /></Button>
                      </div>
                    </div>
                  </div>
                  <ScrollArea className="h-[500px]">
                    <div className="p-3 space-y-2">
                      {weekFixtures.map((fixture) => {
                        const homeTeam = getTeamById(fixture.homeTeamId);
                        const awayTeam = getTeamById(fixture.awayTeamId);
                        if (!homeTeam || !awayTeam) return null;
                        return (
                          <div key={fixture.id} data-testid={`fixture-${fixture.id}`} className={`p-3 rounded-sm border border-[#2C2C35] ${fixture.played ? 'bg-[#0A0A0C]' : 'bg-[#121216]'}`}>
                            <div className="flex items-center gap-3">
                              <div className="flex-1 text-right"><p className="text-sm font-medium text-white truncate">{homeTeam.name}</p></div>
                              <div className="flex items-center gap-2 px-3">
                                {fixture.played ? (
                                  <div className="flex items-center gap-2 bg-[#1A1A1F] px-3 py-1 rounded-sm">
                                    <span className="font-mono font-bold text-lg text-white">{fixture.homeScore}</span>
                                    <span className="text-[#6E6E73]">-</span>
                                    <span className="font-mono font-bold text-lg text-white">{fixture.awayScore}</span>
                                  </div>
                                ) : (
                                  <div className="bg-[#1A1A1F] px-3 py-1 rounded-sm"><span className="text-sm text-[#6E6E73]">vs</span></div>
                                )}
                              </div>
                              <div className="flex-1 text-left"><p className="text-sm font-medium text-white truncate">{awayTeam.name}</p></div>
                            </div>
                            {fixture.played && fixture.events?.filter(e => e.type === 'goal').length > 0 && (
                              <div className="mt-2 pt-2 border-t border-[#2C2C35]">
                                <div className="flex items-start gap-2 text-xs text-[#A0A0A5]">
                                  <SoccerBall size={12} className="mt-0.5 text-[#32D74B]" />
                                  <span>{fixture.events.filter(e => e.type === 'goal').map(e => `${e.player?.name} ${e.minute}'`).join(', ')}</span>
                                </div>
                              </div>
                            )}
                            {!fixture.played && (
                              <div className="mt-2 flex gap-2">
                                <Button size="sm" onClick={(e) => { e.stopPropagation(); simOneFixture(fixture); }} className="flex-1 bg-[#FF006E] hover:bg-[#FF3D8E] text-white h-7 text-xs" data-testid={`league-fixture-quick-${fixture.id}`}>
                                  <Lightning size={12} className="mr-1" weight="fill" /> Quick
                                </Button>
                                <Button size="sm" onClick={(e) => { e.stopPropagation(); openFullSimFixture(fixture); }} className="flex-1 bg-[#00E5FF] hover:bg-[#66F2FF] text-black h-7 text-xs font-semibold" data-testid={`league-fixture-full-${fixture.id}`}>
                                  <Play size={12} className="mr-1" weight="fill" /> Full Sim
                                </Button>
                              </div>
                            )}
                            {fixture.played && (
                              <div className="mt-2">
                                <Button size="sm" variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setHighlightsMatch({
                                      match: { homeTeamName: homeTeam.name, awayTeamName: awayTeam.name, homeScore: fixture.homeScore, awayScore: fixture.awayScore, events: fixture.events, matchRatings: fixture.matchRatings },
                                      homeTeam, awayTeam
                                    });
                                  }}
                                  className="h-6 text-[10px] px-2 bg-transparent border-[#2C2C35] text-white w-full"
                                  data-testid={`league-fixture-hl-${fixture.id}`}>
                                  <Eye size={10} className="mr-1" /> View Full Highlights & Stats
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="awards">
            <CompetitionAwards teams={teams} competition="league" topN={25} />
          </TabsContent>

          <TabsContent value="stats">
            <LeagueWideStats teams={teams} />
          </TabsContent>
        </Tabs>

        {currentWeek > 38 && (
          <div className="mt-8 p-6 card-surface rounded-sm text-center">
            <h2 className="font-heading font-black text-2xl text-[#32D74B] uppercase mb-2">Season Complete!</h2>
            <p className="text-[#A0A0A5]">Champion: {teams.sort((a, b) => b.stats.points - a.stats.points)[0]?.name}</p>
            <div className="flex gap-3 justify-center mt-4 flex-wrap">
              <Button onClick={() => setShowCeremony(true)} className="bg-[#FFD700] hover:bg-[#FFD700]/80 text-black font-bold" data-testid="view-awards-btn">
                <Trophy size={18} className="mr-2" weight="fill" /> View Awards
              </Button>
              <Button onClick={handleAdvanceSeason} className="bg-[#32D74B] hover:bg-[#32D74B]/80 text-black font-bold" data-testid="advance-season-btn-league">
                <ArrowUp size={18} className="mr-2" /> Next Season ({(gameState.year || 2026) + 1})
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Awards ceremony dialog */}
      <Dialog open={showCeremony} onOpenChange={setShowCeremony}>
        <DialogContent className="bg-[#121216] border-[#2C2C35] max-w-2xl" data-testid="league-award-ceremony">
          <AwardCeremony teams={teams} type="league" label="EWFL League" />
        </DialogContent>
      </Dialog>

      <MatchHighlightsModal
        isOpen={!!highlightsMatch}
        onClose={() => setHighlightsMatch(null)}
        match={highlightsMatch?.match}
        homeTeam={highlightsMatch?.homeTeam}
        awayTeam={highlightsMatch?.awayTeam}
        competitionLabel="EWFL League"
      />

      <MatchSimModal
        isOpen={!!simModalMatch}
        onClose={() => setSimModalMatch(null)}
        homeTeam={simModalMatch?.home}
        awayTeam={simModalMatch?.away}
        competitionLabel="EWFL League"
        onResult={(result) => {
          if (simModalMatch?.fixture) applySingleFixtureResult(simModalMatch.fixture, simModalMatch.home, simModalMatch.away, result);
        }}
      />
    </div>
  );
};

// League-wide stats component
const LeagueWideStats = ({ teams }) => {
  const allPlayers = teams.flatMap(t => t.players.map(p => ({ ...p, teamName: t.name })));
  const lg = (p, k) => (p.stats?.league?.[k] || 0);
  const topScorers = [...allPlayers].filter(p => lg(p, 'goals') > 0).sort((a, b) => lg(b, 'goals') - lg(a, 'goals')).slice(0, 100);
  const topAssists = [...allPlayers].filter(p => lg(p, 'assists') > 0).sort((a, b) => lg(b, 'assists') - lg(a, 'assists')).slice(0, 100);
  const topCards = [...allPlayers].filter(p => lg(p, 'yellowCards') > 0).sort((a, b) => lg(b, 'yellowCards') - lg(a, 'yellowCards')).slice(0, 100);
  const topCS = [...allPlayers].filter(p => lg(p, 'cleanSheets') > 0).sort((a, b) => lg(b, 'cleanSheets') - lg(a, 'cleanSheets')).slice(0, 100);
  const injured = allPlayers.filter(p => p.injuredWeeks > 0);

  const StatList = ({ title, data, statKey, color, icon: Icon }) => (
    <div className="card-surface rounded-sm">
      <div className="border-b border-[#2C2C35] px-3 py-2 flex items-center gap-2">
        <Icon size={14} weight="fill" className={color} />
        <span className="text-xs font-bold uppercase text-[#A0A0A5] tracking-wider">{title} (Top 100)</span>
      </div>
      <ScrollArea className="h-[400px]">
      <div className="p-2 space-y-1">
        {data.length === 0 ? <p className="text-[#6E6E73] text-xs p-3">No data yet</p> :
          data.map((p, i) => (
            <div key={p.id} className="flex items-center gap-2 px-2 py-1.5">
              <span className="font-mono text-xs text-[#6E6E73] w-6">{i + 1}</span>
              <span className="nationality-flag text-xs">{p.flag}</span>
              <span className="text-sm text-white flex-1 truncate">{p.name}</span>
              <span className="text-xs text-[#6E6E73] truncate max-w-[80px]">{p.teamName}</span>
              <span className={`font-mono text-sm font-bold ${color}`}>{lg(p, statKey)}</span>
            </div>
          ))}
      </div>
      </ScrollArea>
    </div>
  );

  return (
    <div className="space-y-4">
      {injured.length > 0 && (
        <div className="card-surface rounded-sm p-3">
          <div className="flex items-center gap-2 mb-2">
            <FirstAid size={14} weight="fill" className="text-[#FF6B00]" />
            <span className="text-xs font-bold uppercase text-[#FF6B00] tracking-wider">Injury Report ({injured.length})</span>
          </div>
          <div className="space-y-1">
            {injured.slice(0, 8).map(p => (
              <p key={p.id} className="text-xs text-[#A0A0A5]">{p.flag} {p.name} ({p.teamName}) - <span className="text-[#FF6B00]">{p.injuredWeeks}w out</span></p>
            ))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatList title="Top Scorers" data={topScorers} statKey="goals" color="text-[#FF3B30]" icon={SoccerBall} />
        <StatList title="Top Assists" data={topAssists} statKey="assists" color="text-[#007AFF]" icon={SoccerBall} />
        <StatList title="Most Yellow Cards" data={topCards} statKey="yellowCards" color="text-[#FFD60A]" icon={SoccerBall} />
        <StatList title="Clean Sheets" data={topCS} statKey="cleanSheets" color="text-[#32D74B]" icon={SoccerBall} />
      </div>
    </div>
  );
};
